import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { parseDate } from '@internationalized/date';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext, useFlatData } from '@interxfi/api-client';
import { useIxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsList } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweep-target-stats-query-service-sweep-target-stats-list';
import { useIxcSweepsV1SweepTargetQueryServiceSweepTargetGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-v1-sweep-target-query-service-sweep-target-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { type InputDropdownOption, SelectInput } from '@interxfi/design-system/components/input';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import { usePreferredTimeZone } from '@interxfi/design-system/time-zone-context';
import { useCurrentDate } from '@interxfi/design-system/util';
import {
  IxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsListFilterableFields,
  type IxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsListSweepTargetStatsListRequest,
  type IxcSweepsClearingV1SweepTargetStatsState,
  type IxcSweepsV1SweepTargetState,
} from '@interxfi/types';
import { getDateFormatter } from '@interxfi/utilities/date/formatting';
import { SweepTargetStatsEntityMenu } from '../../../../../../features/sweeps/clearing/target-stats/sweep-target-stats-entity-menu';
import { getSweepTargetStatsStateListItems } from '../../../../../../features/sweeps/clearing/target-stats/sweep-target-stats-list-items';
import { SweepTargetEntityMenu } from '../../../../../../features/sweeps/target/sweep-target-entity-menu';
import { SweepTargetEventSummary } from '../../../../../../features/sweeps/target/sweep-target-event';
import { getSweepTargetStateListItems } from '../../../../../../features/sweeps/target/sweep-target-list-items';
import { getSweepTargetDetailPath } from '../../../../../../features/sweeps/target/sweep-target-routing';
import { SweepTargetStatusPill } from '../../../../../../features/sweeps/target/sweep-target-status-pill';
import { SweepTargetSummaryBar } from '../../../../../../features/sweeps/target/sweep-target-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/sweep/target/_target/$sweepTargetId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-target-${params.sweepTargetId}`,
        label: 'Target',
        to: getSweepTargetDetailPath(params.sweepTargetId),
        suffix: (
          <CopyButton
            content={params.sweepTargetId}
            label="Copy Sweep Target ID"
            tooltip={{ copy: params.sweepTargetId, copied: 'Copied Sweep Target ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Target'),
  component: SweepTargetDetail,
});

const monthFormatter = getDateFormatter('en-US', {
  month: 'long',
  year: 'numeric',
});

function getListItems(data: IxcSweepsV1SweepTargetState | undefined) {
  const listItems = getSweepTargetStateListItems(data);

  return {
    general: {
      left: [
        { ...listItems.data.name, title: 'Security' },
        listItems.data.fullSymbol,
        listItems.data.cusip,
        listItems.data.fundFamily,
      ] satisfies ReadonlyListItem[],
      right: [listItems.sweepTargetId, listItems.data.assetId] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep target', 'view this sweep target');

function getSweepTargetStatsRequest(sweepTargetId: string) {
  return {
    query: {
      filters: [
        {
          field: {
            name: IxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsListFilterableFields.SweepTargetId,
            type: {
              value: sweepTargetId,
            },
          },
        },
      ],
    },
  } as const satisfies IxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsListSweepTargetStatsListRequest;
}

function getSweepTargetStatItems(data: IxcSweepsClearingV1SweepTargetStatsState | undefined) {
  const items = getSweepTargetStatsStateListItems(data);

  return [items.data.sweptBalance, items.data.dividends, items.data.dividendDate, items.data.paid.summary];
}

function SweepTargetDetail() {
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const { sweepTargetId } = Route.useParams();
  const request = { sweepTargetId } as const;
  const { data, error, isPending } = useIxcSweepsV1SweepTargetQueryServiceSweepTargetGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepTarget), [data?.sweepTarget]);
  const sweepTargetListRequest = useMemo(
    () => (activeTenantIsClearing ? getSweepTargetStatsRequest(sweepTargetId) : undefined),
    [activeTenantIsClearing, sweepTargetId],
  );
  const {
    data: sweepTargetData,
    isPending: sweepTargetStatsPending,
    error: sweepTargetStatsError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useIxcSweepsClearingV1SweepTargetStatsQueryServiceSweepTargetStatsList(sweepTargetListRequest);
  const showSweepTargetStatsPending = Boolean(sweepTargetStatsPending || sweepTargetStatsError);
  const [selectedStats, setSelectedStats] = useState<IxcSweepsClearingV1SweepTargetStatsState | undefined>();
  const statsListItems = useMemo(() => getSweepTargetStatItems(selectedStats), [selectedStats]);
  const flatSweepStats = useFlatData(sweepTargetData, 'sweepTargetStats');
  const timeZone = usePreferredTimeZone();

  useEffect(() => {
    if (flatSweepStats.length) {
      setSelectedStats((prev) => {
        if (prev) {
          return prev;
        }

        return flatSweepStats[0];
      });
    }
  }, [flatSweepStats]);

  const currentDate = useCurrentDate();
  const currentMonthStart = useMemo(() => {
    currentDate.date.set({ day: 1 });
    return currentDate.date;
  }, [currentDate]);

  const statMonthOptions: InputDropdownOption[] = useMemo(() => {
    if (!flatSweepStats.length) {
      return [
        {
          label: monthFormatter.format(currentMonthStart.toDate(timeZone)),
          value: currentMonthStart.toString(),
        },
      ];
    }

    return flatSweepStats.map((stat) => {
      const firstDayOfMonth = stat.firstDayOfMonth;
      const date = parseDate(firstDayOfMonth);

      return {
        label: monthFormatter.format(date.toDate(timeZone)),
        value: firstDayOfMonth,
      };
    });
  }, [currentMonthStart, flatSweepStats, timeZone]);

  const renderedMonth = selectedStats?.firstDayOfMonth ?? currentMonthStart.toString();

  const handleSelectedStatChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const matchingStat = flatSweepStats.find((stat) => stat.firstDayOfMonth === e.target.value);

      if (matchingStat) {
        setSelectedStats(matchingStat);
      }
    },
    [flatSweepStats],
  );

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepTargetStatusPill isPending={showPending} size="large" status={data?.sweepTarget?.status} />
            </HorizontalStack>

            <SweepTargetEntityMenu {...request} />
          </PageHeader>

          <SweepTargetSummaryBar isPending={showPending} sweepTargetId={sweepTargetId} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepTargetEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {activeTenantIsClearing ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
              <Typography.Level3>Statistics</Typography.Level3>

              <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                <SelectInput
                  allowClear={false}
                  hideLabel
                  label="Select Month"
                  name="select-month"
                  onChange={handleSelectedStatChange}
                  options={statMonthOptions}
                  pagination={{ hasNextPage, fetchNextPage, isFetchingNextPage }}
                  placement="bottom-end"
                  value={renderedMonth}
                  variant="button-light-small"
                />
                <SweepTargetStatsEntityMenu firstDayOfMonth={renderedMonth} sweepTargetId={sweepTargetId} />
              </HorizontalStack>
            </HorizontalStack>

            <AppReadonlyList isPending={showSweepTargetStatsPending} items={statsListItems} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
